import { Button, Dialog, Flex, ProgressIndicator, Text, Spinner } from '@applyboard/crystal-ui';
import { InfoOutlineIcon } from '@applyboard/ui-icons';
import { useState } from 'react';
import styled from '@emotion/styled';
import { useVisaProbability } from '../../data/useVisaProbability';
import { useParams } from 'react-router-dom';

const InfoLightIcon = () => (
  <Text variant="bodyS" contrast="mid" align="center" as="div">
    <Flex align="center" as="div" gap={2}>
      <InfoOutlineIcon />
    </Flex>
  </Text>
);

export function VisaForecast() {
  const [isOpen, setIsOpen] = useState(false);
  const { id } = useParams();

  const { visaProbability, isLoadingVisaProbability } = useVisaProbability(id ?? '');

  const prediction = visaProbability?.attributes?.model_prediction;
  const firstLevel = prediction && Object.keys(prediction)[0];
  const regionScoreObj = firstLevel ? prediction[firstLevel] : undefined;
  const firstRegion = regionScoreObj && Object.keys(regionScoreObj)[0];
  const score = firstRegion ? regionScoreObj[firstRegion] : null;

  const displayValue = score != null ? `${score}%` : 'N/A';
  const displayValueInProgressBar = score != null ? score : 0;

  if (isLoadingVisaProbability) {
    return (
      <Flex direction="column" align="center">
        <Flex>
          <Spinner />
        </Flex>
      </Flex>
    );
  }

  return (
    <>
      <Flex as="div" gap={1} direction="column">
        <Flex align="center" as="div" gap={2}>
          <Text variant="bodyS" contrast="mid">
            Visa forcast
          </Text>
          <Button
            size="sm"
            variant="transparent"
            aria-label="Open menu"
            leadIcon={InfoLightIcon}
            onClick={() => setIsOpen(true)}
          />
          <Text variant="bodyS" contrast="mid">
            {displayValue}
          </Text>
        </Flex>
        <ProgressionWrapper>
          <ProgressIndicator
            aria-label="visa forcast progress"
            variant="linear"
            size="sm"
            total={100}
            value={displayValueInProgressBar}
          />
        </ProgressionWrapper>
      </Flex>

      <Dialog
        open={isOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) setIsOpen(false);
        }}
      >
        <Dialog.Heading>About visa forecast</Dialog.Heading>
        <Dialog.Content>
          {score != null ? (
            <>
              <Text variant="bodyM">
                The visa forecast shows the estimated chance that a student will be approved for a Canadian study
                permit. The percentage (from 0% to 100%) is based on key factors like nationality, age, and gender,
                using historical data and machine learning to compare the student to similar past applicants.
              </Text>
              <br />
              <Text variant="bodyM">Please note:</Text>
              <ol>
                <li>
                  <Text variant="bodyM">
                    Based on the inherent uncertainty of IRCC decision making, the visa forecast leverages a
                    probabilistic data model to predict the likelihood of visa success based on known factors about the
                    applicant. Real outcomes may vary.
                  </Text>
                </li>
                <li>
                  <Text variant="bodyM">
                    The model will continue to improve over time as it learns from more data and outcomes, making
                    predictions more accurate and helpful.
                  </Text>
                </li>
                <li>
                  <Text variant="bodyM">
                    The model does not include all factors considered by immigration officers.
                  </Text>
                </li>
                <li>
                  <Text variant="bodyM">
                    The model assumes the student meets eligibility requirements and can get accepted by a designated
                    Canadian school.
                  </Text>
                </li>
              </ol>
              <Text variant="bodyM">
                This tool is meant to guide schools in supporting students, but it is not legal or immigration advice.
              </Text>
            </>
          ) : (
            <Text variant="bodyM">The visa forecast is not available for this student at the moment.</Text>
          )}
        </Dialog.Content>
      </Dialog>
    </>
  );
}

const ProgressionWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexGrow: 1,
  '> div': {
    width: '100%',
    '> div:first-of-type': {
      display: 'none',
    },
  },
});
